/**
 * CodeBlock — Shiki-backed syntax-highlighted code surface for CSK.
 *
 * Replaces the original token-free `<pre><code>` renderer with a Shiki
 * highlighter pre-loaded with the five grammars the CSK demos actually
 * use: Agda, TypeScript, TSX, Python, and Haskell. Selecting only those
 * grammars keeps the bundled highlighter ~50× smaller than Shiki's
 * default web bundle.
 *
 * The component degrades gracefully:
 *   1. The first synchronous render emits a non-highlighted
 *      `<pre><code>{code}</code></pre>` so SSR / first-paint never
 *      blocks on Shiki initialisation. Crucially, the raw text is
 *      always present in the DOM, so search and accessibility tooling
 *      see the source even before the highlighter resolves.
 *   2. On the client, an effect calls the shared highlighter
 *      singleton, then swaps in the Shiki HTML once it resolves.
 *   3. If the highlighter fails (network error, unsupported language),
 *      the fallback rendering is preserved.
 *
 * Theme tokens come from `tokens.data.json` — the surrounding chrome
 * (background, foreground, border) is driven by `GRAY[900]` / `SAFE[300]`
 * exactly as the prior CodeBlock did, so a token recolour propagates
 * here without a code change. Syntax colors are sourced from the
 * `github-dark-default` Shiki theme; future palettes can be added by
 * extending {@link SUPPORTED_CODE_THEMES} in the highlighter module.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useMemo, useState } from "react";
import { FONT, GRAY, SAFE } from "../../design-system/tokens.js";
import { TrustedHtml } from "../TrustedHtml.js";
import { createTrustedHtml, type TrustedHtmlValue } from "../../security/trustedHtml.js";
import {
  getCodeHighlighter,
  normalizeCodeLanguage,
  type SupportedCodeLanguage,
  type SupportedCodeTheme,
} from "./highlighter.js";

/**
 * Public language vocabulary exposed by the CodeBlock surface.
 *
 * Accepts both the canonical Shiki names (`typescript`, `python`,
 * `haskell`) and the short aliases (`ts`, `py`, `hs`) so callers can
 * use whichever convention matches their content. `text` skips
 * highlighting altogether, mirroring the legacy CodeBlock's plain-text
 * fallback.
 */
export type CodeLanguage =
  | "agda"
  | "typescript"
  | "ts"
  | "tsx"
  | "python"
  | "py"
  | "haskell"
  | "hs"
  | "sql"
  | "text";

export interface CodeBlockProps {
  /** Source text rendered inside the code surface. */
  readonly code: string;
  /**
   * Grammar key. Defaults to `text` (no syntax highlighting beyond
   * the monospace surface). Aliases (`ts`, `py`, `hs`) are normalised
   * to the canonical Shiki names.
   */
  readonly language?: CodeLanguage;
  /** Optional title rendered above the code surface. */
  readonly title?: string;
  /**
   * Shiki theme name. Defaults to `github-dark-default` so the surface
   * matches the existing CSK code aesthetic. Pass
   * `github-light-default` for a light surface.
   */
  readonly theme?: SupportedCodeTheme;
  /** Accessible label override. Defaults to `${language} code block`. */
  readonly ariaLabel?: string;
}

interface HighlightState {
  readonly status: "idle" | "ready" | "error";
  readonly html?: TrustedHtmlValue;
}

function constrainShikiSurface(html: string): string {
  return html.replace(
    /<pre([^>]*)style="([^"]*)"/,
    '<pre$1style="$2; margin: 0; max-width: 100%; overflow-x: auto;"',
  );
}

const TITLE_STYLE: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 500,
  color: GRAY[700],
  marginBottom: "0.25rem",
};

const PRE_STYLE: React.CSSProperties = {
  boxSizing: "border-box",
  fontFamily: FONT.mono,
  fontSize: "0.875rem",
  backgroundColor: GRAY[900],
  color: SAFE[300],
  maxWidth: "100%",
  padding: "1rem",
  borderRadius: "0.5rem",
  overflowX: "auto",
  margin: 0,
  width: "100%",
};

/** Token-aligned chrome wrapped around the highlighted Shiki HTML. */
const SHIKI_WRAPPER_STYLE: React.CSSProperties = {
  backgroundColor: GRAY[900],
  borderRadius: "0.5rem",
  boxSizing: "border-box",
  fontFamily: FONT.mono,
  fontSize: "0.875rem",
  margin: 0,
  maxWidth: "100%",
  overflowX: "auto",
  padding: "1rem",
  width: "100%",
};

/**
 * Render a code snippet with Shiki syntax highlighting, with a non-
 * highlighted fallback during the initial paint.
 */
export const CodeBlock = React.memo(function CodeBlock({
  code,
  language = "text",
  title,
  theme = "github-dark-default",
  ariaLabel,
}: CodeBlockProps): React.JSX.Element {
  const resolvedLanguage: SupportedCodeLanguage = useMemo(
    () => normalizeCodeLanguage(language),
    [language],
  );

  const [state, setState] = useState<HighlightState>({ status: "idle" });

  useEffect(() => {
    if (resolvedLanguage === "text") {
      // No grammar lookup required; the fallback `<pre>` already
      // renders the source verbatim.
      setState({ status: "idle" });
      return;
    }
    setState({ status: "idle" });
    let cancelled = false;
    getCodeHighlighter()
      .then((highlighter) => {
        if (cancelled) return;
        const html = highlighter.codeToHtml(code, {
          lang: resolvedLanguage,
          theme,
        });
        setState({
          status: "ready",
          html: createTrustedHtml(constrainShikiSurface(html), "shiki"),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ status: "error" });
      });
    return (): void => {
      cancelled = true;
    };
  }, [code, resolvedLanguage, theme]);

  const accessibleLabel = ariaLabel ?? `${resolvedLanguage} code block`;

  return (
    <div style={{ marginTop: "1rem", maxWidth: "100%", minWidth: 0 }}>
      {title !== undefined && <div style={TITLE_STYLE}>{title}</div>}
      {state.status === "ready" && state.html !== undefined ? (
        <TrustedHtml
          aria-label={accessibleLabel}
          data-csk-code={resolvedLanguage}
          data-csk-code-status="ready"
          html={state.html}
          role="region"
          style={SHIKI_WRAPPER_STYLE}
        />
      ) : (
        <pre
          aria-label={accessibleLabel}
          data-csk-code={resolvedLanguage}
          data-csk-code-status={state.status === "error" ? "error" : "fallback"}
          style={PRE_STYLE}
        >
          <code>{code}</code>
        </pre>
      )}
    </div>
  );
});
